import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Type2QuestionsPage.css';
import popupImage from '../images/popup.png';

const h = React.createElement;

const Type2QuestionsPage = () => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [openQuestions, setOpenQuestions] = useState([]);
  const [isCompleteEnabled, setIsCompleteEnabled] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [showPopup, setShowPopup] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isFromProfile = location.state?.isFromProfile || false;

  useEffect(() => {
    const storedQuestions = localStorage.getItem('type2Questions');
    if (storedQuestions) {
      try {
        const allQuestions = JSON.parse(storedQuestions);
        const filtered = allQuestions.filter(
          (q) => q.text !== 'Does your skin look pink in light?'
        );
        setQuestions(filtered);
        setOpenQuestions(filtered.map((q) => q.id)); // start all open
      } catch {
        console.error('Could not parse type2Questions from localStorage');
      }
    } else {
      console.error('No questions found in local storage');
    }
  }, []);

  const handleAnswerChange = (questionId, answerId) => {
    setAnswers((prev) => {
      const updated = { ...prev, [questionId]: answerId };

      // Enable Complete when all (except id=13) are answered
      const allAnswered = questions
        .filter((q) => q.id !== 13)
        .every((q) => updated[q.id] !== undefined);
      setIsCompleteEnabled(allAnswered);

      return updated;
    });

    // Collapse this question after answering
    setOpenQuestions((prev) => prev.filter((id) => id !== questionId));
  };

  const toggleQuestion = (questionId) => {
    setOpenQuestions((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleBackButtonClick = () => {
    if (!isFromProfile) {
      navigate('/skin-type', { state: { isFromProfile } });
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    if (isFromProfile) {
      navigate('/user-profile');
    } else {
      navigate('/skin-concerns');
    }
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!isCompleteEnabled) return;

    const selectedAnswerIds = Object.values(answers);
    if (selectedAnswerIds.length === 0) {
      setPopupMessage('No answers selected.');
      setShowPopup(true);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://dev-api.curu.com.au/api/skin-type/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ answers: selectedAnswerIds }),
      });

      const result = await response.json();

      if (response.ok) {
        setPopupMessage(
          '<div class="popup-result-skin-type-QA">' +
            result.name +
            '</div>' +
            '<div class="popup-additional-skin-type-QA">Based on your answers, this is your skin type.</div>'
        );
        sessionStorage.setItem(
          'skin-type',
          JSON.stringify({ id: result.id, name: result.name })
        );
      } else {
        setPopupMessage('Failed to submit answers.');
      }
    } catch {
      setPopupMessage('An error occurred while submitting your answers.');
    }

    setShowPopup(true);
  };

  // ---- Render helpers ----
  const renderAnswer = (q, answer) =>
    h(
      'li',
      { key: answer.id, className: 'answer-option-skin-type-QA' },
      h(
        'label',
        null,
        h('input', {
          type: 'radio',
          name: `question-${q.id}`,
          value: answer.id,
          checked: answers[q.id] === answer.id,
          onChange: function () {
            handleAnswerChange(q.id, answer.id);
          },
        }),
        ' ',
        answer.text
      )
    );

  const renderQuestion = (q) => {
    const isOpen = openQuestions.includes(q.id);
    const isAnswered = answers[q.id] != null;

    return h(
      'div',
      {
        key: q.id,
        className:
          'section-skin-type-QA' + (isOpen ? '' : ' folded') + (isAnswered ? ' answered' : ''),
        role: 'group',
        'aria-labelledby': `q-${q.id}-label`,
      },
      // HEADER (click to toggle)
      h(
        'h6',
        {
          className: 'question-skin-type-QA',
          id: `q-${q.id}-label`,
          role: 'button',
          tabIndex: 0,
          'aria-expanded': String(isOpen),
          'aria-controls': `q-${q.id}-body`,
          onClick: function () {
            toggleQuestion(q.id);
          },
          onKeyDown: function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleQuestion(q.id);
            }
          },
        },
        h('span', null, q.text),
        !isOpen &&
          isAnswered &&
          h(
            'div',
            { className: 'custom-tickmark-container-skin-type-QA' },
            h('div', { className: 'custom-tickmark-skin-type-QA' })
          )
      ),
      // BODY WRAPPER (collapsible via CSS)
      h(
        'div',
        {
          id: `q-${q.id}-body`,
          className: 'section-body-skin-type-QA',
          'aria-hidden': String(!isOpen),
        },
        h(
          'ul',
          { className: 'answer-list-skin-type-QA' },
          q.answers.map((ans) => renderAnswer(q, ans))
        )
      )
    );
  };

  return h(
    'div',
    { className: 'page-container-skin-type-QA' },
    h(
      'div',
      { className: 'qa-form-container-skin-type-QA' },
      h('h1', { className: 'title-skin-type-QA' }, 'Find Your Skin Type'),

      // Questions
      questions
        .filter((q) => q.answers && q.answers.length > 0)
        .map((q) => renderQuestion(q)),

      // Buttons
      h(
        'div',
        { className: 'buttons-container-skin-type-QA' },
        !isFromProfile &&
          h(
            'button',
            { className: 'back-button-skin-type-QA', onClick: handleBackButtonClick },
            'Back'
          ),
        h(
          'button',
          {
            className:
              'btn-complete-skin-type-QA ' + (isCompleteEnabled ? '' : 'disabled'),
            onClick: isCompleteEnabled ? handleSubmit : undefined,
            disabled: !isCompleteEnabled,
          },
          'Complete'
        )
      )
    ),

    // Popup
    showPopup &&
      h(
        'div',
        { className: 'popup-overlay-skin-type-QA' },
        h(
          'div',
          { className: 'popup-content-skin-type-QA' },
          h('img', {
            src: popupImage,
            alt: 'User',
            className: 'popup-image-skin-type-QA',
          }),
          h('p', { dangerouslySetInnerHTML: { __html: popupMessage } }),
          h('button', { onClick: handleClosePopup }, 'Close')
        )
      )
  );
};

export default Type2QuestionsPage;
